import { ref, computed, reactive, watch } from 'vue';

/**
 * Vue 3 Composable for form validation
 * Provides reactive validation with rules and error handling
 */
export function useValidation() {
  const errors = reactive({});
  const touched = reactive({});

  /**
   * Validation rules
   */
  const rules = {
    required: (value) => {
      if (value === null || value === undefined || value === '') {
        return 'This field is required';
      }
      if (typeof value === 'object' && value.id === -1) {
        return 'Please select a valid option';
      }
      return null;
    },
    
    requiredIf: (condition) => (value) => {
      if (condition()) {
        return rules.required(value);
      }
      return null;
    },

    minLength: (min) => (value) => {
      if (value && value.length < min) {
        return `Minimum length is ${min} characters`;
      }
      return null;
    },

    email: (value) => {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Please enter a valid email address';
      }
      return null;
    }
  };

  /**
   * Register a field for validation
   * @param {string} fieldName - Name of the field
   * @param {ref} fieldValue - Reactive reference to field value
   * @param {Array} validationRules - Array of validation rules
   */
  function registerField(fieldName, fieldValue, validationRules = []) {
    // Initialize touched state
    touched[fieldName] = false;

    // Watch field value for changes
    watch(fieldValue, () => {
      if (touched[fieldName]) {
        validateField(fieldName, fieldValue.value, validationRules);
      }
    }, { immediate: false });

    // Mark field as touched when it loses focus
    const onBlur = () => {
      touched[fieldName] = true;
      validateField(fieldName, fieldValue.value, validationRules);
    };

    return { onBlur };
  }

  /**
   * Validate a single field
   * @param {string} fieldName - Name of the field
   * @param {any} value - Current field value
   * @param {Array} validationRules - Array of validation rules
   */
  function validateField(fieldName, value, validationRules) {
    const fieldErrors = [];

    for (const rule of validationRules) {
      const error = rule(value);
      if (error) {
        fieldErrors.push(error);
        break; // Stop at first error
      }
    }

    if (fieldErrors.length > 0) {
      errors[fieldName] = fieldErrors[0];
    } else {
      delete errors[fieldName];
    }
  }

  /**
   * Validate all registered fields
   * @param {Object} fields - Object containing field names and their validation rules
   * @param {Object} values - Object containing current field values
   */
  function validateAll(fields, values) {
    let isValid = true;

    for (const [fieldName, validationRules] of Object.entries(fields)) {
      touched[fieldName] = true;
      validateField(fieldName, values[fieldName], validationRules);
      
      if (errors[fieldName]) {
        isValid = false;
      }
    }

    return isValid;
  }

  /**
   * Clear all errors and touched states
   */
  function clearValidation() {
    Object.keys(errors).forEach(key => delete errors[key]);
    Object.keys(touched).forEach(key => delete touched[key]);
  }

  /**
   * Clear validation for a specific field
   * @param {string} fieldName - Name of the field to clear
   */
  function clearField(fieldName) {
    delete errors[fieldName];
    touched[fieldName] = false;
  }

  /**
   * Check if form is valid (no errors)
   */
  const isValid = computed(() => {
    return Object.keys(errors).length === 0;
  });

  /**
   * Get error for a specific field
   * @param {string} fieldName - Name of the field
   */
  const getError = (fieldName) => {
    return touched[fieldName] ? errors[fieldName] : null;
  };

  /**
   * Check if a field has an error
   * @param {string} fieldName - Name of the field
   */
  const hasError = (fieldName) => {
    return touched[fieldName] && !!errors[fieldName];
  };

  /**
   * Mark a field as touched
   * @param {string} fieldName - Name of the field
   */
  const touch = (fieldName) => {
    touched[fieldName] = true;
  };

  return {
    // State
    errors,
    touched,
    isValid,

    // Rules
    rules,

    // Methods
    registerField,
    validateField,
    validateAll,
    clearValidation,
    clearField,
    getError,
    hasError,
    touch
  };
}

/**
 * Specific validation composable for SendAdminRequest form
 */
export function useSendAdminRequestValidation() {
  const validation = useValidation();
  const { rules } = validation;

  // Form field refs
  const selectedAdminRequest = ref(null);
  const selectedRequestType = ref(null);
  const selectedModificationType = ref(null);

  // Validation rules for each field
  const validationRules = computed(() => ({
    selectedAdminRequest: [rules.required],
    selectedRequestType: [
      rules.requiredIf(() => 
        selectedAdminRequest.value && selectedAdminRequest.value.id === 206
      )
    ],
    selectedModificationType: [
      rules.requiredIf(() => 
        selectedAdminRequest.value && selectedAdminRequest.value.id === 174
      )
    ]
  }));

  // Field values for validation
  const fieldValues = computed(() => ({
    selectedAdminRequest: selectedAdminRequest.value,
    selectedRequestType: selectedRequestType.value,
    selectedModificationType: selectedModificationType.value
  }));

  // Validate all fields
  const validateForm = () => {
    return validation.validateAll(validationRules.value, fieldValues.value);
  };

  // Check if form can be submitted
  const canSubmit = computed(() => {
    // Manual validation check for dynamic fields
    if (!selectedAdminRequest.value || selectedAdminRequest.value.id === -1) {
      return false;
    }

    if (selectedAdminRequest.value.id === 206) {
      if (!selectedRequestType.value || selectedRequestType.value.id === -1) {
        return false;
      }
    }

    if (selectedAdminRequest.value.id === 174) {
      if (!selectedModificationType.value || selectedModificationType.value.id === -1) {
        return false;
      }
    }

    return validation.isValid.value;
  });

  return {
    // Form field refs
    selectedAdminRequest,
    selectedRequestType,
    selectedModificationType,

    // Validation
    ...validation,
    validateForm,
    canSubmit,
    validationRules,
    fieldValues
  };
}